import { Bell, IdCard, KeyRound, Lock, LogOut, User } from 'lucide-react'
import { useState } from 'react'
import { Form, Link, useFetcher, useRouteLoaderData } from 'react-router'
import { type loader as rootLoader } from '#app/root.tsx'
import { type loader as accountSummaryLoader } from '#app/routes/resources/account-summary.tsx'
import { ThemeSwitch } from '#app/routes/resources/theme-switch.tsx'
import { cn } from '#app/utils/misc.tsx'
import { useUser, userHasPermission } from '#app/utils/user.ts'
import { MembershipStatus } from './membership-status.tsx'
import {
	Sheet,
	SheetClose,
	SheetContent,
	SheetHeader,
	SheetTitle,
} from './ui/sheet.tsx'
import { UserAvatar } from './user-avatar.tsx'

const sheetItemClass =
	'flex min-h-touch items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors'

export function AvatarMenu() {
	const [open, setOpen] = useState(false)
	const user = useUser()
	const rootData = useRouteLoaderData<typeof rootLoader>('root')
	const isAdmin =
		userHasPermission(user, 'read:content:any') ||
		userHasPermission(user, 'read:user:any')

	const fetcher = useFetcher<typeof accountSummaryLoader>()
	const summary = fetcher.data

	function handleOpenChange(nextOpen: boolean) {
		setOpen(nextOpen)
		// Lazy-load account summary on first open. Fire from the open handler —
		// no useEffect — per CLAUDE.md guidance.
		if (
			nextOpen &&
			fetcher.data === undefined &&
			fetcher.state === 'idle'
		) {
			void fetcher.load('/resources/account-summary')
		}
	}

	const hasPassword = summary?.hasPassword

	return (
		<Sheet open={open} onOpenChange={handleOpenChange}>
			<button
				type="button"
				onClick={() => handleOpenChange(true)}
				aria-label="Ouvrir le menu de compte"
				aria-haspopup="dialog"
				aria-expanded={open}
				className="focus-visible:ring-ring relative z-10 inline-flex h-touch w-touch cursor-pointer touch-manipulation items-center justify-center rounded-full outline-hidden transition-transform focus-visible:ring-2 focus-visible:ring-offset-2 active:scale-[0.95]"
			>
				<UserAvatar
					name={user.name}
					username={user.username}
					className="ring-primary/20 h-9 w-9 text-xs ring-2"
				/>
			</button>

			<SheetContent side="right">
				<SheetHeader>
					<SheetTitle className="sr-only">Menu de compte</SheetTitle>
					<div className="flex items-center gap-4 pb-3">
						<UserAvatar
							name={user.name}
							username={user.username}
							className="h-16 w-16 shrink-0 text-xl"
						/>
						<div className="min-w-0 flex-1">
							<div className="truncate text-base font-semibold">
								{user.name ?? user.username}
							</div>
							<div className="text-muted-foreground truncate text-sm">
								@{user.username}
							</div>
						</div>
					</div>
					{summary?.user?.plqMemberId != null ? (
						<div className="pt-1">
							<MembershipStatus variant="compact" data={summary.user} />
						</div>
					) : null}
				</SheetHeader>

				<nav aria-label="Compte" className="flex flex-col gap-1">
					<SheetClose asChild>
						<Link
							to="/settings/profile/carte"
							prefetch="intent"
							className={cn(sheetItemClass, 'text-foreground hover:bg-muted')}
						>
							<IdCard className="h-5 w-5 shrink-0" aria-hidden="true" />
							Mon adhésion
						</Link>
					</SheetClose>

					<SheetClose asChild>
						<Link
							to="/settings/profile"
							prefetch="intent"
							className={cn(sheetItemClass, 'text-foreground hover:bg-muted')}
						>
							<User className="h-5 w-5 shrink-0" aria-hidden="true" />
							Modifier le profil
						</Link>
					</SheetClose>

					<SheetClose asChild>
						<Link
							to="/settings/profile/notifications"
							prefetch="intent"
							className={cn(sheetItemClass, 'text-foreground hover:bg-muted')}
						>
							<Bell className="h-5 w-5 shrink-0" aria-hidden="true" />
							Notifications par courriel
						</Link>
					</SheetClose>

					<SheetClose asChild>
						<Link
							to={
								hasPassword === false
									? '/settings/profile/password/create'
									: '/settings/profile/password'
							}
							prefetch="intent"
							className={cn(sheetItemClass, 'text-foreground hover:bg-muted')}
						>
							<KeyRound className="h-5 w-5 shrink-0" aria-hidden="true" />
							Mot de passe
						</Link>
					</SheetClose>

					{isAdmin && (
						<SheetClose asChild>
							<Link
								to="/admin"
								prefetch="intent"
								className={cn(sheetItemClass, 'text-foreground hover:bg-muted')}
							>
								<Lock className="h-5 w-5 shrink-0" aria-hidden="true" />
								Administration
							</Link>
						</SheetClose>
					)}
				</nav>

				<div className="border-border mt-2 flex items-center justify-between gap-2 border-t pt-2">
					<Form action="/logout" method="POST">
						<button
							type="submit"
							className={cn(
								sheetItemClass,
								'text-foreground-destructive hover:bg-destructive/10',
							)}
						>
							<LogOut className="h-5 w-5 shrink-0" aria-hidden="true" />
							Déconnexion
						</button>
					</Form>
					<ThemeSwitch
						userPreference={rootData?.requestInfo.userPrefs.theme}
					/>
				</div>
			</SheetContent>
		</Sheet>
	)
}
